import { useQuery } from "@tanstack/react-query";
import type { Aptos } from "@aptos-labs/ts-sdk";
import { getBkfcAptos } from "./aptos";
import { requireBkfcModuleAddress } from "./config";

// cached at module scope — the collection address is fixed once the
// contract's configure() has run
let cachedCollectionAddress: string | null = null;

async function getCollectionAddress(aptos: Aptos): Promise<string> {
  if (cachedCollectionAddress) return cachedCollectionAddress;
  const [address] = await aptos.view<[string]>({
    payload: {
      function: `${requireBkfcModuleAddress()}::certificate::collection_address`,
      functionArguments: [],
    },
  });
  cachedCollectionAddress = address;
  return address;
}

export interface OwnedBkfcCertificate {
  address: string;
  uid: string;
  tokenUri: string;
  isSoulbound: boolean;
}

interface UseOwnedCertificatesResult {
  certificates: OwnedBkfcCertificate[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// Ported from the Aptos reference app (apps/web/src/hooks/useOwnedCertificates.ts).
// Uses the Indexer (aptos.getOwnedDigitalAssets) straight from the browser —
// there is no on-chain "list of a wallet's tokens" to read directly. The UID
// is the token name; that's how the contract mints certificates.
export function useOwnedCertificates(
  ownerAddress: string | undefined,
): UseOwnedCertificatesResult {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bkfc-owned-certificates", ownerAddress],
    enabled: !!ownerAddress,
    queryFn: async (): Promise<OwnedBkfcCertificate[]> => {
      const aptos = getBkfcAptos();
      const collectionAddress = await getCollectionAddress(aptos);
      const owned = await aptos.getOwnedDigitalAssets({
        ownerAddress: ownerAddress!,
      });

      return owned
        .filter(
          (item) =>
            item.current_token_data?.current_collection?.collection_id ===
            collectionAddress,
        )
        .map((item) => ({
          address: item.token_data_id,
          uid: item.current_token_data?.token_name ?? "",
          tokenUri: item.current_token_data?.token_uri ?? "",
          isSoulbound: item.is_soulbound_v2 ?? false,
        }));
    },
  });

  return {
    certificates: data ?? null,
    isLoading,
    error: error ? "Couldn't load certificates from the indexer." : null,
    refetch,
  };
}
